import React from 'react';
import { AxiosError, isAxiosError } from 'axios';

import { t as tLegacy } from '~/app/shared/components/l10n/l10n';

function isValidationError<T>(error: APIValidationError<T>): error is ValidationError<T> {
    return !!(error as ValidationError<T>).validation;
}

export const getErrorMessage = <T extends object>(
    error: APIValidationError<T>,
    errorMessage?: string,
    registeredFields?: Record<string, unknown>
): React.ReactElement => {
    if (isValidationError(error)) {
        return (
            <ul className="mb-0">
                {Object.keys(error.validation)
                    .filter(key => registeredFields?.[key] || typeof error.validation[key] === 'string')
                    .map(key => (
                        <li key={key}>
                            {registeredFields?.[key]
                                ? `${registeredFields[key]}: ${error.validation[key]}`
                                : error.validation[key]}
                        </li>
                    ))}
            </ul>
        );
    }

    return <>{errorMessage || (error as Error).message}</>;
};

export interface ValidationError<T> {
    validation: T;
}

export interface ValidationInternalApiError<T> {
    data: Record<keyof T, string | unknown[]>;
    errorCode: string;
}

interface OpenApiError<T> {
    property: T;
    message: string;
    errorCode: string;
}

export interface ValidationOpenApiError<T> {
    errorCode: string;
    errors: OpenApiError<keyof T>[];
}

export type APIValidationError<T> = AxiosError<ValidationInternalApiError<T> | ValidationOpenApiError<T>> | ValidationError<T>;

/**
 * Check if the error is an OpenApi error
 * InvalidInput can be caused by Required, Range, StringLength, Regex validation
 * @param data
 */
export function isOpenApiInputValidationError<T>(
    data: ValidationInternalApiError<T> | ValidationOpenApiError<T>
): data is ValidationOpenApiError<T> {
    return isOpenApiError(data) && data.errorCode === 'InvalidInput' && Array.isArray(data.errors);
}

/**
 * Check if the error is an OpenApi error
 * errorCode can be BadFilterRequest, BadSortRequest, InvalidInput, or any domain related errors
 * @param data
 */
export function isOpenApiError<T>(
    data: ValidationInternalApiError<T> | ValidationOpenApiError<T>
): data is ValidationOpenApiError<T> {
    return 'errorCode' in data;
}

type NormalizeProperty<T> = {
    [K in keyof T as `${Uncapitalize<string & K>}`]: string;
};

/**
 * convert PascalCase to camelCase
 * @param property string
 */
const normalizeOpenApiProperty = <T extends string = string>(property: T) => {
    return (property.charAt(0).toLowerCase() + property.slice(1)) as `${Uncapitalize<string & T>}`;
};

function getOpenApiErrorValidation<T extends object = Record<string, never>, FormValues extends object = Record<string, never>>(
    data: ValidationOpenApiError<T>,
    values?: FormValues,
    mapErrorMessages?: (errorCode: string, field: string, values?: FormValues) => string
) {
    return data.errors.reduce(
        (accum, { property, message }) => {
            const prop = normalizeOpenApiProperty(property as string);
            return {
                ...accum,
                [prop]: mapErrorMessages?.(message, prop, values) || message,
            };
        },
        {} as Partial<NormalizeProperty<T>>
    );
}

function getInternalApiErrorValidation<T extends object, TValues extends object>(
    data: ValidationInternalApiError<T>,
    values?: TValues,
    mapErrorMessages?: (errorCode: string, field: string, values?: TValues) => string
) {
    return Object.entries(data).reduce((accum, [property, item]) => {
        const errorItem = Array.isArray(item) ? item[0] : item;

        // handle error in case that it has object shape
        if (typeof errorItem === 'object') {
            // Data can contain variables that make the errors a bit dynamic.
            // Like a specific number for max length that vary depending on config/context
            const data = Object.entries(errorItem.data).reduce((obj, [key, value]) => {
                obj[key.toUpperCase()] = value;
                return obj;
            }, {} as TValues);

            return {
                ...accum,
                [property]:
                    mapErrorMessages?.(errorItem.errorCode, property, data) || tLegacy(`ERROR_CODE_${errorItem.errorCode}`, data),
                textParams: data,
            };
        }

        return {
            ...accum,
            [property]:
                mapErrorMessages?.(errorItem, property, values) ||
                tLegacy(`ERROR_CODE_${errorItem}`, { VALUE: values?.[property] ?? property }),
        };
    }, {} as T);
}

/**
 * Get the validation error object from the API error
 *
 * @param error validation or API error
 * @param values optional form values to map error messages
 * @param mapErrorMessages optional function to map error messages
 * @returns validation error object with field as key and error message as value,
 * if key is empty, it means domain related error
 */
export const getAPIErrorValidation = <
    T extends object = Record<string, never>,
    FormValues extends object = Record<string, never>,
>(
    error: APIValidationError<T>,
    values?: FormValues,
    mapErrorMessages?: (errorCode: string, field: string, values?: FormValues) => string
): T => {
    if (isValidationError(error)) {
        return error.validation;
    }

    if (isAxiosError(error) && error.response) {
        const { data } = error.response;

        // Return empty object if data is not an object
        if (typeof data !== 'object') {
            return {} as T;
        }

        // OpenApi InvalidInput errors
        if (isOpenApiInputValidationError(data)) {
            return getOpenApiErrorValidation(data, values, mapErrorMessages) as T;
        }

        // other OpenApi errors - errorCode
        if (isOpenApiError(data)) {
            return { '': mapErrorMessages?.(data.errorCode, '', values) || data.errorCode } as T;
        }

        // handle internal API errors
        return getInternalApiErrorValidation(data, values, mapErrorMessages);
    }

    return {} as T;
};
